import React,{useState} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {trackSocialShare} from '../utils/analytics';

const {FiShare2,FiCopy,FiCheck,FiX,FiFacebook,FiTwitter,FiMessageCircle,FiMail,FiPhone,FiDownload}=FiIcons;

const SocialShare=({weatherData,typhoonData,isOpen,onClose,location='Iligan City'})=> {
const [copied,setCopied]=useState(false);
const [shareType,setShareType]=useState('weather');// 'weather','typhoon','safety'

// Generate shareable content based on current conditions
const generateShareContent=(type)=> {
const baseUrl='https://weather.goiligan.com';
const timestamp=new Date().toLocaleString('en-PH',{timeZone: 'Asia/Manila'});

const content={
weather: {
title: `${location} Weather Update`,
text: `Current weather in ${location}: ${weatherData?.regional?.mindanao?.temperature || '25-30°C'},${weatherData?.regional?.mindanao?.condition || 'Partly cloudy'}. ${weatherData?.regional?.mindanao?.rainfall || 'Isolated thunderstorms'} expected. Stay safe! 🌤️`,
hashtags: '#IliganWeather #NorthernMindanao #WeatherUpdate #Philippines',
url: baseUrl
},
typhoon: {
title: `🌀 TYPHOON ALERT - ${location}`,
text: `🚨 TYPHOON "${typhoonData?.localName || 'ACTIVE'}" ${typhoonData?.internationalName ? `(${typhoonData.internationalName})` : ''} affecting ${location}!

Position: ${typhoonData?.position || 'Approaching Philippines'}
Winds: ${typhoonData?.maxWinds || 'Strong'} kph
Signal: ${typhoonData?.signals?.[0]?.level ? `#${typhoonData.signals[0].level}` : 'Monitor updates'}

⚠️ Follow evacuation orders! Stay safe!
📞 Emergency: 911 | Iligan DRRMO: (063) 221-4078`,
hashtags: '#TyphoonAlert #IliganSafety #EmergencyUpdate #Philippines #NorthernMindanao',
url: baseUrl
},
safety: {
title: `${location} Safety Information`,
text: `📍 ${location} Emergency Contacts & Weather Safety:

🚨 Emergency Hotline: 911
🏛️ Iligan DRRMO: (063) 221-4078
🌊 PAGASA Northern Mindanao: (088) 856-1443
⛑️ Coast Guard: (063) 221-2207

Stay informed with real-time weather updates!
${typhoonData ? '🌀 ACTIVE TYPHOON - Monitor closely!' : '☀️ Weather monitoring active'}`,
hashtags: '#IliganSafety #EmergencyContacts #WeatherSafety #NorthernMindanao #Philippines',
url: baseUrl
}
};

return content[type] || content.weather;
};

// Social media sharing functions
const shareToFacebook=(content)=> {
const fbUrl=`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(content.url)}&quote=${encodeURIComponent(content.text)}`;
window.open(fbUrl,'_blank','width=600,height=400');
trackSocialShare('Facebook',shareType,weatherData?.regional?.mindanao?.condition);
};

const shareToTwitter=(content)=> {
const tweetText=`${content.text}\n\n${content.hashtags}`;
const twitterUrl=`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(content.url)}`;
window.open(twitterUrl,'_blank','width=600,height=400');
trackSocialShare('Twitter',shareType,weatherData?.regional?.mindanao?.condition);
};

const shareToWhatsApp=(content)=> {
const whatsappText=`${content.text}\n\n${content.url}`;
const whatsappUrl=`https://wa.me/?text=${encodeURIComponent(whatsappText)}`;
window.open(whatsappUrl,'_blank');
trackSocialShare('WhatsApp',shareType,weatherData?.regional?.mindanao?.condition);
};

const shareToMessenger=(content)=> {
const messengerUrl=`https://www.facebook.com/dialog/send?link=${encodeURIComponent(content.url)}&app_id=YOUR_APP_ID`;
window.open(messengerUrl,'_blank','width=600,height=400');
trackSocialShare('Messenger',shareType,weatherData?.regional?.mindanao?.condition);
};

const shareViaEmail=(content)=> {
const subject=encodeURIComponent(content.title);
const body=encodeURIComponent(`${content.text}\n\nView live updates: ${content.url}`);
window.location.href=`mailto:?subject=${subject}&body=${body}`;
trackSocialShare('Email',shareType,weatherData?.regional?.mindanao?.condition);
};

const shareViaSMS=(content)=> {
const smsText=encodeURIComponent(`${content.title}\n\n${content.text}\n\n${content.url}`);
window.location.href=`sms:?body=${smsText}`;
trackSocialShare('SMS',shareType,weatherData?.regional?.mindanao?.condition);
};

// Copy to clipboard
const copyToClipboard=async (content)=> {
const textToCopy=`${content.title}\n\n${content.text}\n\n${content.url}`;
try {
await navigator.clipboard.writeText(textToCopy);
setCopied(true);
setTimeout(()=> setCopied(false),2000);
trackSocialShare('Clipboard',shareType,weatherData?.regional?.mindanao?.condition);
} catch (err) {
// Fallback for older browsers
const textArea=document.createElement('textarea');
textArea.value=textToCopy;
document.body.appendChild(textArea);
textArea.select();
document.execCommand('copy');
document.body.removeChild(textArea);
setCopied(true);
setTimeout(()=> setCopied(false),2000);
}
};

// Generate image for sharing (simple text-based for now)
const downloadShareImage=async (content)=> {
const canvas=document.createElement('canvas');
const ctx=canvas.getContext('2d');
canvas.width=1200;
canvas.height=630;// Facebook recommended size

// Background gradient
const gradient=ctx.createLinearGradient(0,0,1200,630);
gradient.addColorStop(0,'#3B82F6');
gradient.addColorStop(1,'#1E40AF');
ctx.fillStyle=gradient;
ctx.fillRect(0,0,1200,630);

// Text content
ctx.fillStyle='white';
ctx.font='bold 48px Arial,sans-serif';
ctx.textAlign='center';

// Title
ctx.fillText(content.title,600,150);

// Weather info
ctx.font='32px Arial,sans-serif';
const lines=content.text.split('\n').slice(0,4);
lines.forEach((line,index)=> {
ctx.fillText(line,600,250 + (index * 50));
});

// Footer
ctx.font='bold 24px Arial,sans-serif';
ctx.fillText('weather.goiligan.com',600,550);

// Convert to blob and download
canvas.toBlob((blob)=> {
const url=URL.createObjectURL(blob);
const a=document.createElement('a');
a.href=url;
a.download=`iligan-weather-${shareType}-${Date.now()}.png`;
document.body.appendChild(a);
a.click();
document.body.removeChild(a);
URL.revokeObjectURL(url);
trackSocialShare('Image Download',shareType,weatherData?.regional?.mindanao?.condition);
},'image/png');
};

const currentContent=generateShareContent(shareType);

const shareButtons=[
{name: 'Facebook',icon: FiFacebook,color: 'bg-blue-600 hover:bg-blue-700',action: ()=> shareToFacebook(currentContent)},
{name: 'Twitter',icon: FiTwitter,color: 'bg-sky-500 hover:bg-sky-600',action: ()=> shareToTwitter(currentContent)},
{name: 'WhatsApp',icon: FiMessageCircle,color: 'bg-green-500 hover:bg-green-600',action: ()=> shareToWhatsApp(currentContent)},
{name: 'Messenger',icon: FiMessageCircle,color: 'bg-blue-500 hover:bg-blue-600',action: ()=> shareToMessenger(currentContent)},
{name: 'Email',icon: FiMail,color: 'bg-gray-600 hover:bg-gray-700',action: ()=> shareViaEmail(currentContent)},
{name: 'SMS',icon: FiPhone,color: 'bg-orange-500 hover:bg-orange-600',action: ()=> shareViaSMS(currentContent)}
];

if (!isOpen) return null;

return (
<AnimatePresence>
<motion.div
initial={{opacity: 0}}
animate={{opacity: 1}}
exit={{opacity: 0}}
className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
onClick={onClose}
>
<motion.div
initial={{scale: 0.9,opacity: 0}}
animate={{scale: 1,opacity: 1}}
exit={{scale: 0.9,opacity: 0}}
className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
onClick={(e)=> e.stopPropagation()}
>
{/* Header */}
<div className="flex items-center justify-between p-6 border-b border-gray-200">
<div className="flex items-center">
<SafeIcon icon={FiShare2} className="text-2xl text-blue-600 mr-3" />
<h2 className="text-xl font-bold text-gray-900">Share Weather Update</h2>
</div>
<button
onClick={onClose}
className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
>
<SafeIcon icon={FiX} className="text-gray-500" />
</button>
</div>

{/* Share Type Selector */}
<div className="p-6 border-b border-gray-100">
<h3 className="text-sm font-semibold text-gray-700 mb-3">What would you like to share?</h3>
<div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
<button
onClick={()=> setShareType('weather')}
className={`p-3 rounded-lg border text-left transition-colors ${shareType==='weather' ? 'bg-blue-50 border-blue-300 text-blue-800' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}`}
>
<div className="font-medium text-sm">Current Weather</div>
<div className="text-xs text-gray-600">Temperature & conditions</div>
</button>

{typhoonData && (
<button
onClick={()=> setShareType('typhoon')}
className={`p-3 rounded-lg border text-left transition-colors ${shareType==='typhoon' ? 'bg-red-50 border-red-300 text-red-800' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}`}
>
<div className="font-medium text-sm">🌀 Typhoon Alert</div>
<div className="text-xs text-gray-600">Emergency information</div>
</button>
)}

<button
onClick={()=> setShareType('safety')}
className={`p-3 rounded-lg border text-left transition-colors ${shareType==='safety' ? 'bg-green-50 border-green-300 text-green-800' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}`}
>
<div className="font-medium text-sm">Safety Info</div>
<div className="text-xs text-gray-600">Emergency contacts</div>
</button>
</div>
</div>

{/* Preview */}
<div className="p-6 border-b border-gray-100">
<h3 className="text-sm font-semibold text-gray-700 mb-3">Preview</h3>
<div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
<div className="font-bold text-gray-900 mb-2">{currentContent.title}</div>
<div className="text-sm text-gray-700 whitespace-pre-line mb-2">{currentContent.text}</div>
<div className="text-xs text-blue-600">{currentContent.url}</div>
{currentContent.hashtags && (
<div className="text-xs text-blue-500 mt-1">{currentContent.hashtags}</div>
)}
</div>
</div>

{/* Share Buttons */}
<div className="p-6">
<h3 className="text-sm font-semibold text-gray-700 mb-4">Share via:</h3>

{/* Social Media Grid */}
<div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
{shareButtons.map((button)=> (
<motion.button
key={button.name}
whileHover={{scale: 1.02}}
whileTap={{scale: 0.98}}
onClick={button.action}
className={`flex items-center justify-center space-x-2 p-3 text-white rounded-lg transition-colors ${button.color}`}
>
<SafeIcon icon={button.icon} className="text-lg" />
<span className="font-medium text-sm">{button.name}</span>
</motion.button>
))}
</div>

{/* Additional Actions */}
<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
<button
onClick={()=> copyToClipboard(currentContent)}
className={`flex items-center justify-center space-x-2 p-3 border rounded-lg transition-colors ${copied ? 'bg-green-50 border-green-300 text-green-700' : 'bg-gray-50 border-gray-300 hover:bg-gray-100'}`}
>
<SafeIcon icon={copied ? FiCheck : FiCopy} className="text-lg" />
<span className="font-medium text-sm">
{copied ? 'Copied!' : 'Copy Text'}
</span>
</button>

<button
onClick={()=> downloadShareImage(currentContent)}
className="flex items-center justify-center space-x-2 p-3 bg-purple-50 border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors"
>
<SafeIcon icon={FiDownload} className="text-lg" />
<span className="font-medium text-sm">Download Image</span>
</button>
</div>
</div>

{/* Important Notice */}
<div className="px-6 pb-6">
<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
<p className="text-xs text-yellow-800">
<strong>⚠️ Important:</strong> When sharing emergency information,always encourage recipients to verify with official sources (PAGASA,Iligan DRRMO) and follow evacuation orders immediately.
</p>
</div>
</div>
</motion.div>
</motion.div>
</AnimatePresence>
);
};

export default SocialShare;